import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, BookmarkPlus, Save, Wand2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { MAX_TYPES, MAX_VALUES, MAX_VARIANTS, syncRows, typeError, variantErrors, variantLabel } from "@/lib/variants";
import { btnOutline } from "@/components/app/ui";

const cell = "h-8 w-full rounded-md border bg-white px-2 text-[13px] outline-none transition focus:ring-2";
const ok = "border-slate-300 focus:border-blue-600 focus:ring-blue-600/15";
const bad = "border-red-500 focus:ring-red-500/15";

function Chips({ values, onChange, placeholder, testId }) {
  const [text, setText] = useState("");
  const add = (raw) => {
    const incoming = raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (!incoming.length) return;
    const next = [...values];
    for (const v of incoming) {
      if (next.some((x) => x.toLowerCase() === v.toLowerCase())) continue;
      if (next.length >= MAX_VALUES) { toast.error(`At most ${MAX_VALUES} values per option.`); break; }
      next.push(v.slice(0, 40));
    }
    onChange(next); setText("");
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-slate-300 bg-white px-1.5 py-1 min-h-8 focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-600/15">
      {values.map((v) => (
        <span key={v} className="inline-flex items-center gap-1 rounded bg-blue-50 text-blue-700 border border-blue-100 pl-2 pr-1 text-[12px] font-medium">
          {v}<button type="button" onClick={() => onChange(values.filter((x) => x !== v))} aria-label={`Remove ${v}`} className="w-4 h-4 rounded hover:bg-blue-100 flex items-center justify-center"><X size={11} /></button>
        </span>
      ))}
      <input value={text} onChange={(e) => { if (e.target.value.includes(",")) add(e.target.value); else setText(e.target.value); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(text); } else if (e.key === "Backspace" && !text && values.length) onChange(values.slice(0, -1)); }}
        onBlur={() => add(text)} placeholder={values.length ? "Add another…" : placeholder} aria-label="Option values" data-testid={testId}
        className="flex-1 min-w-[90px] bg-transparent text-[13px] outline-none px-1 h-6" />
    </div>
  );
}

/**
 * Variants of a product: option types with values, and a generated table with one row per combination,
 * each with its own price, optional compare-at price, stock, SKU and image. Option names and values can be
 * saved as a template and applied to other products (prices and stock are per product).
 */
export default function VariantsEditor({ types, rows, onChange, images = [], basePrice = "", showErrors = false }) {
  const [templates, setTemplates] = useState([]);
  const [naming, setNaming] = useState(false);
  const [tplName, setTplName] = useState("");
  const [saving, setSaving] = useState(false);
  const [allPrice, setAllPrice] = useState("");

  const loadTemplates = useCallback(() => api.get("/vendor/attribute-templates", { params: { kind: "variants" } }).then(({ data }) => setTemplates(data)).catch(() => {}), []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const tErr = useMemo(() => typeError(types), [types]);
  const rErrs = useMemo(() => variantErrors(types, rows), [types, rows]);
  const liveTypes = types.filter((t) => t.name.trim() && t.values.length);
  const tooMany = rows.length > MAX_VARIANTS;

  const setTypes = (next) => onChange({ types: next, rows: syncRows(next, rows, { price: basePrice }) });
  const patchType = (i, patch) => setTypes(types.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const setRow = (i, k, v) => onChange({ types, rows: rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });

  const applyTemplate = (t) => {
    if (types.length && !window.confirm(`Replace the options you've entered with “${t.name}”? Prices and stock will need to be re-entered.`)) return;
    const next = t.option_types.map((o) => ({ name: o.name, values: [...o.values] }));
    onChange({ types: next, rows: syncRows(next, [], { price: basePrice }) });
    toast.success(`Applied “${t.name}”. Now set a price and stock for each variant.`);
  };

  const saveTemplate = async () => {
    if (tErr || !types.length) return toast.error(tErr || "Add an option first.");
    if (!tplName.trim()) return toast.error("Give the template a name.");
    setSaving(true);
    try {
      await api.post("/vendor/attribute-templates", { name: tplName.trim(), kind: "variants", option_types: types.map((t) => ({ name: t.name.trim(), values: t.values })) });
      toast.success(`Template “${tplName.trim()}” saved.`);
      setTplName(""); setNaming(false); loadTemplates();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const applyAll = () => {
    const n = Number(allPrice);
    if (!(n > 0)) return toast.error("Enter a price greater than 0.");
    onChange({ types, rows: rows.map((r) => ({ ...r, price: String(n) })) });
  };

  return (
    <div data-testid="variants-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[12px] font-semibold text-slate-700">Variants</div>
          <p className="text-[11px] text-slate-500">Sell one product in different options, each with its own price and stock — e.g. Format: Paperback ₹900, Hardcover ₹1150, or Size: S ₹399, XL ₹449.</p>
        </div>
        <select aria-label="Apply an option template" data-testid="variant-template-select" value="" disabled={!templates.length}
          onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); if (t) applyTemplate(t); }}
          className="h-8 rounded-lg border border-slate-300 bg-white px-2 text-[12px] text-neutral-800 disabled:opacity-50 max-w-[200px]">
          <option value="">Use option template…</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.option_types.map((o) => o.name).join(", ")})</option>)}
        </select>
      </div>

      <div className="mt-2.5 space-y-2">
        {types.map((t, i) => (
          <div key={i} className="grid grid-cols-[130px_1fr_32px] gap-2 items-start" data-testid={`variant-type-${i}`}>
            <input value={t.name} onChange={(e) => patchType(i, { name: e.target.value })} maxLength={30} placeholder="Option, e.g. Size" aria-label="Option name" data-testid={`variant-type-name-${i}`}
              aria-invalid={showErrors && !t.name.trim() ? "true" : "false"} className={`${cell} ${showErrors && !t.name.trim() ? bad : ok}`} />
            <Chips values={t.values} onChange={(v) => patchType(i, { values: v })} placeholder="Type a value and press Enter (S, M, L)" testId={`variant-type-values-${i}`} />
            <button type="button" onClick={() => setTypes(types.filter((_, j) => j !== i))} aria-label="Remove option" data-testid={`variant-type-remove-${i}`} className="w-8 h-8 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      {showErrors && tErr && <p role="alert" className="mt-1 text-[11px] text-red-600">{tErr}</p>}

      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => setTypes([...types, { name: "", values: [] }])} disabled={types.length >= MAX_TYPES} data-testid="variant-add-option" className={`${btnOutline} disabled:opacity-50`}>
          <Plus size={14} /> {types.length ? "Add another option" : "Add option"}
        </button>
        {types.length > 0 && !naming && <button type="button" onClick={() => setNaming(true)} data-testid="variant-save-template" className="ml-auto inline-flex items-center gap-1 text-[12px] font-semibold text-slate-600 hover:text-blue-600"><BookmarkPlus size={14} /> Save options as template</button>}
        {naming && (
          <div className="ml-auto flex items-center gap-1.5">
            <input value={tplName} onChange={(e) => setTplName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveTemplate(); } }} autoFocus maxLength={60} placeholder="Template name" aria-label="Template name" data-testid="variant-template-name" className={`${cell} ${ok} w-40`} />
            <button type="button" onClick={saveTemplate} disabled={saving} data-testid="variant-template-confirm" className={btnOutline}><Save size={13} /> {saving ? "Saving…" : "Save"}</button>
            <button type="button" onClick={() => { setNaming(false); setTplName(""); }} aria-label="Cancel" className="w-8 h-8 rounded-md text-slate-400 hover:bg-slate-100 flex items-center justify-center"><X size={14} /></button>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
            <div className="text-[12px] font-semibold text-slate-700">{rows.length} variant{rows.length > 1 ? "s" : ""}</div>
            <div className="flex items-center gap-1.5">
              <input value={allPrice} onChange={(e) => setAllPrice(e.target.value)} type="number" min="0" step="0.01" placeholder="Price for all" aria-label="Price for all variants" data-testid="variant-all-price" className={`${cell} ${ok} w-28`} />
              <button type="button" onClick={applyAll} data-testid="variant-apply-all" className={btnOutline}><Wand2 size={13} /> Apply</button>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 overflow-x-auto">
            <table className="w-full text-[13px] min-w-[620px]">
              <thead className="bg-slate-50 text-[11px] font-semibold text-slate-500">
                <tr><th className="text-left px-2 py-1.5">Variant</th><th className="text-left px-1 py-1.5 w-[110px]">Price (₹) *</th><th className="text-left px-1 py-1.5 w-[110px]">Compare at</th>
                  <th className="text-left px-1 py-1.5 w-[80px]">Stock *</th><th className="text-left px-1 py-1.5 w-[110px]">SKU</th>{images.length > 0 && <th className="text-left px-1 py-1.5 w-[110px]">Image</th>}</tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const e = showErrors ? rErrs[i] : undefined;
                  return (
                    <React.Fragment key={r.id || `${i}-${variantLabel(r.options, liveTypes)}`}>
                      <tr className="border-t border-slate-100" data-testid={`variant-row-${i}`}>
                        <td className="px-2 py-1.5 font-medium text-neutral-900 whitespace-nowrap">{variantLabel(r.options, liveTypes)}</td>
                        <td className="px-1 py-1"><input value={r.price} onChange={(ev) => setRow(i, "price", ev.target.value)} type="number" min="0" step="0.01" aria-label={`Price of ${variantLabel(r.options, liveTypes)}`} data-testid={`variant-price-${i}`} aria-invalid={e ? "true" : "false"} className={`${cell} ${e ? bad : ok}`} /></td>
                        <td className="px-1 py-1"><input value={r.compare_at_price} onChange={(ev) => setRow(i, "compare_at_price", ev.target.value)} type="number" min="0" step="0.01" aria-label="Compare-at price" data-testid={`variant-compare-${i}`} className={`${cell} ${ok}`} /></td>
                        <td className="px-1 py-1"><input value={r.stock} onChange={(ev) => setRow(i, "stock", ev.target.value)} type="number" min="0" step="1" aria-label="Stock" data-testid={`variant-stock-${i}`} className={`${cell} ${ok}`} /></td>
                        <td className="px-1 py-1"><input value={r.sku} onChange={(ev) => setRow(i, "sku", ev.target.value)} maxLength={60} aria-label="SKU" data-testid={`variant-sku-${i}`} className={`${cell} ${ok}`} /></td>
                        {images.length > 0 && (
                          <td className="px-1 py-1">
                            <select value={r.image} onChange={(ev) => setRow(i, "image", ev.target.value)} aria-label="Variant image" className={`${cell} ${ok}`}>
                              <option value="">Default</option>
                              {images.map((u, k) => <option key={u} value={u}>Image {k + 1}</option>)}
                            </select>
                          </td>
                        )}
                      </tr>
                      {e && <tr><td colSpan={6} className="px-2 pb-1.5"><p role="alert" className="text-[11px] text-red-600">{variantLabel(r.options, liveTypes)}: {e}</p></td></tr>}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {tooMany && <p role="alert" className="mt-1 text-[11px] text-red-600">At most {MAX_VARIANTS} variants — remove some values.</p>}
          <p className="mt-1 text-[11px] text-slate-500">The product&apos;s price shown in lists is the lowest variant price; total stock is the sum of the variants. Set stock to 0 for a combination you don&apos;t sell.</p>
        </div>
      )}
    </div>
  );
}
